import { IRuleCondition } from "../../../../../../@types/Worflows";

/** Everything on a condition except `id`, which the server owns and the form must not send back. */
type IEditableConditionField = Exclude<keyof IRuleCondition, "id">;

/**
 * Every editable field of a rule condition, in the order they are declared on IRuleCondition.
 *
 * The builder cannot spread the API's condition into the form: the server sends fields the form
 * must not own (`id`), and Formik needs a stable key set so switching rule type clears the
 * previous type's answers. So the set is explicit — and adding a rule type without adding its
 * fields here is the bug this list exists to prevent: the workflow saves, then reopens with only
 * the type selected (EN-5089).
 */
export const RULE_CONDITION_FORM_FIELDS = [
  "conditionComparatorType",
  "threshold",
  "triggerType",
  "excludeBadBlows",
  "measurementType",
  "surveyId",
  "surveyQuestionIds",
  "surveyQuestionOptionIds",
  "surveyDomain",
  "inactivityDays",
  "executionTime",
  "timezone",
  "fibricheckStatus",
  "aliveCorEcgStatus",
  "sleepStage",
  "activityMetric",
  "dateEventType",
  "daysOffset",
  "targetDate",
  "spirometryStatus",
  "occurrenceCount",
  "occurrencePeriod",
] as const satisfies readonly IEditableConditionField[];

/** Fields IRuleCondition declares that the list above forgot — must be `never`. */
type IUnmappedConditionField = Exclude<
  IEditableConditionField,
  (typeof RULE_CONDITION_FORM_FIELDS)[number]
>;

type IAssertNever<T extends never> = T;

/**
 * Compile-time exhaustiveness. Adding a field to IRuleCondition without listing it above fails
 * `tsc` here, instead of silently dropping it when the workflow is reopened for editing.
 */
export type IConditionFieldsAreExhaustive =
  IAssertNever<IUnmappedConditionField>;

/** The saved condition as the form's initial values — every field present, unset ones undefined. */
export function toRuleConditionFormValues(
  condition?: IRuleCondition
): IRuleCondition {
  return Object.fromEntries(
    RULE_CONDITION_FORM_FIELDS.map((field) => [field, condition?.[field]])
  );
}

/** A blank condition, used when the rule type changes and the previous answers must go. */
export function emptyRuleConditionFormValues(): IRuleCondition {
  return toRuleConditionFormValues(undefined);
}
